import React, { Component } from 'react';
import PropTypes from 'prop-types';
import 'whatwg-fetch';
import fetch from 'node-fetch';
import _ from 'lodash';
import * as Scroll from 'react-scroll';
import { addMonthsToDate, decodeHTML, hashToObjectArray, isEquivalent, searchToObject } from './helpers';
import './find-polyfill';

import Sidebar from './components/Sidebar';
import PostList from './components/PostList';

//
//  ARCHIVE APP
//
//  This application is displayed on various archive and search pages.
//  It fetches the page and post type from the DOM generated by WordPress,
//  fetches the appropriate content from the WP REST API, and allows the
//  user to filter and otherwise interact with the results.
//

class Archive extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filterOptions: this.props.filterOptions,
      orderedActiveFilterList: new Array(),
      pagination: this.props.pagination,
      query: null, // The base query set by the type of page the app is displayed on. Set to a knownQueryTypes by componentDidUpdate
      response: null, // Set when a fetch is completed successfully
      wpData: {
        dataLoaded: false,
        taxonomies: [ // Default taxonomies we wish to fetch.
          {
            'displayName': 'Capability',
            'name': 'capability',
            'terms': null,
          },
          {
            'displayName': 'Category',
            'name': 'categories',
            'terms': null,
          },
          {
            'displayName': 'Topic',
            'name': 'topics',
            'terms': null,
          },
          {
            'displayName': 'Resource Type',
            'name': 'resourceType',
            'terms': null,
          },
          {
            'displayName': 'Status',
            'name': 'status',
            'terms': null,
          }
        ],
      },
    };
  };

  componentDidMount() {
    // Get taxonomies from WP immediately on load.
    this.fetchTaxonomies();
    // Setup popstate handling for back/forward functionality
    window.onpopstate = function(e) {
      this.handlePopState(e);
    }.bind(this);
  };

  componentDidUpdate() {
    // Check if all taxonomy terms are loaded, if so try to fetch posts
    if (this.areTaxonomiesLoaded()) {
      // Set the initial state based on what data we're able to get from the DOM
      // and trigger the initial fetchPosts()
      let WordPressArchiveType = this.getWordPressArchiveType();
      if (WordPressArchiveType) {
        this.setState({
          query: WordPressArchiveType
        }, () =>  this.init()); 
      } else {
        this.init();
      }
    }
  };

  init() { // Called by componentDidUpdate() when loading of wpData is complete. Sets up intitial filterOptions state and performs the first posts fetch
    let filterOptions = _.cloneDeep(this.state.filterOptions);

    // Create filterOptions for the taxonomies we fetched (capability, categories, topics, resourceType)
    for (let t=0; t<this.state.wpData.taxonomies.length; t++) {
      let taxonomy = this.state.wpData.taxonomies[t];
      for (let i=0; i<taxonomy.terms.length; i++) {
        let obj = {};
        obj.active = false;
        obj.children = (function() {
          // Loop through the array again to find items that have a parent attribute that
          // matches this item's id. Return an array of matches.
          let childArr = [];
          for (let c=0; c<taxonomy.terms.length; c++) {
            if (taxonomy.terms[c].parent === taxonomy.terms[i].id) {
              childArr.push(taxonomy.terms[c]);
            }
          }
          return childArr;
        })();
        obj.label = decodeHTML(taxonomy.terms[i].name);
        obj.obj = taxonomy.terms[i];
        obj.relation = (function() {
          if (taxonomy.terms[i].parent === 0) {
            return 'parent';
          }
          else {
            return 'child';
          }
        })();
        obj.taxonomy = {
          displayName: taxonomy.displayName,
          name: taxonomy.name,
        };
        obj.type = 'taxonomy';
        obj.value = taxonomy.terms[i].id;
        obj.parent = (function() {
          if (taxonomy.terms[i].parent === 0) {
            return null;
          }
          else {
            return taxonomy.terms[i].parent;
          }
        })();
        filterOptions[taxonomy.name].push(obj);
      }
    }

    // Add resourceType taxonomy as a child of the Resource item in postType (issue #186)
    let newResoureType = _.find(filterOptions.postType, function(o) { return o.value === 'resource'; });
    newResoureType.children = filterOptions.resourceType;

    // Set the initial filterOptions.postType active state based on what type of page the app lives on.
    if (this.state.query.pageType === 'search') {
      for (let i=0; i<filterOptions.postType.length; i++) { // Activate all post types for search
        filterOptions.postType[i].active = true;
      }
    }
    if (this.state.query.pageType === 'archive') {
      let postType = this.state.query.postType;
      if (postType === 'resource' || postType === 'project' || postType === 'news-events') {
        for (let i=0; i<filterOptions.postType.length; i++) { // Activate only the single post type for resources, projects or news & events
          if (filterOptions.postType[i].value === postType) {
            filterOptions.postType[i].active = true;
          }
        }
      }
      else { // We're on a non-post-type archive (eg. a taxonomy term archive or something)
        if (this.state.query.filter.name === 'status') { // We're viewing a Status taxonomy term archive. Status only applies to projects, so only enable that one.
          for (let i=0; i<filterOptions.postType.length; i++) { // Activate all post types
            if (filterOptions.postType[i].value === 'project') {
              filterOptions.postType[i].active = true;
            }
          }
        }
        else { // For all other archives, activate all post prop-types
          for (let i=0; i<filterOptions.postType.length; i++) {
            filterOptions.postType[i].active = true;
          }
        }
      }

      // Check if there's a filter applied already on these pages (eg. if a user clicked a taxonmy term link), and if so activate it 
      if (typeof this.state.query.filter !== 'undefined') {
        let filterName = this.state.query.filter.name;
        if (typeof filterOptions[filterName] !== 'undefined') {
          for (let i=0; i<filterOptions[filterName].length; i++) {
            if (filterOptions[filterName][i].value === this.state.query.filter.value) { // We found it the right filter, so activate it.
              filterOptions[filterName][i].active = true;

              // Is does this term have a parent? If so, activate it
              if (filterOptions[filterName][i].parent !== null) {
                for (let c=0; c<filterOptions[filterName].length; c++) {
                  if (filterOptions[filterName][c].value === filterOptions[filterName][i].parent) { // Found the parent, so activate it.
                    filterOptions[filterName][c].active = true;
                  }
                }
              }
            }
          }
        }
      }
    }

    // Add category terms as children to the News & Events post type
    for (let i=0; i<filterOptions.postType.length; i++) {
      if (filterOptions.postType[i].value === 'news-events') {
        filterOptions.postType[i].children = filterOptions.categories;
        filterOptions.postType[i].relation = 'parent';
      }
      if (filterOptions.postType[i].value === 'project') {
        filterOptions.postType[i].children = filterOptions.status;
        filterOptions.postType[i].relation = 'parent';
      }
    }


    // Test filter options set here
    // filterOptions.date[1].active = true;
    // filterOptions.categories[0].active = true;

    // console.log('Filter options ----------------------------');
    // console.log(filterOptions);
    this.setState({
      filterOptions: filterOptions
    }, () => this.checkforURLData() ); // checkforURLData before we execute a fetchPosts. checkforURLData ultimately calls fetchPosts() to do the initial fetch
  };

  //
  //  HISTORY STATE / URL HANDLING
  //
    checkforURLData() {
      if (window.location.hash.length > 0) {
        let urlVars = searchToObject(window.location.hash);
        let urlVarsArr = hashToObjectArray(window.location.hash);
        let query = _.cloneDeep(this.state.query);
        let filterOptions = _.cloneDeep(this.state.filterOptions);
        let postTypesToShow = [];

        for (let i=0; i<urlVarsArr.length; i++) {
          if (urlVarsArr[i].prop === 'p') {
            query.page = parseInt(urlVarsArr[i].value, 10);
          }
          if (urlVarsArr[i].prop === 'capability' || urlVarsArr[i].prop === 'resourceType' || urlVarsArr[i].prop === 'categories' || urlVarsArr[i].prop === 'date' || urlVarsArr[i].prop === 'topics') {
            let prop = urlVarsArr[i].prop;
            for (let p=0; p<filterOptions[prop].length; p++) {
              if (filterOptions[prop][p].value == urlVarsArr[i].value) { // This == throws an warning, but we're keeping it b/c urlvars that are numbers come back as strings.
                filterOptions[prop][p].active = true;
              }
            }
          }
          if (urlVarsArr[i].prop === 'postType') {
            let prop = urlVarsArr[i].prop;
            // There are post types in the URL, so disable all them in filter options by default so we can turn them on selectively later.
            // If none exist in th eURL, we'll just use the default post types set in init()
            for (let p=0; p<filterOptions[prop].length; p++) {
              filterOptions[prop][p].active = false;
            }
            for (let p=0; p<filterOptions[prop].length; p++) { // Now pass the postTypes that were in URL variables to the postTypesToShow array
              if (filterOptions[prop][p].value == urlVarsArr[i].value) {
                postTypesToShow.push(filterOptions[prop][p]);
              }
            }
          }
        }

        // Loop through post types and show them. If none were passed, we'll just use the default post types set in init()
        for (let i=0; i<postTypesToShow.length; i++) {
          for (let p=0; p<filterOptions.postType.length; p++) {
            if (filterOptions.postType[p].value === postTypesToShow[i].value) {
              filterOptions.postType[p].active = true;
            }
          }
        }

        this.setState({
          query: query,
          filterOptions: filterOptions,
        }, function() {
          this.firstRunActiveFilterGeneration();
          this.fetchPosts();
        }.bind(this));
      }
      else { // No hash, just fetch posts
        this.firstRunActiveFilterGeneration();
        this.fetchPosts();
      }
    };
    handlePopState(e) {
      if (e.state) { // Get the history state we passed to the browser earlier with addHistoryState() and apply it to app state
        this.setState({
          filterOptions: e.state.filterOptions,
          query: e.state.query,
          response: e.state.response,
        });
      }
    };
    addHistoryState() { // Called when fetchPosts
      let historyState = { // Create the historyState object to apply to app state when this history state is accessed
        filterOptions: this.state.filterOptions,
        query: this.state.query,
        response: this.state.response,
      };

      let url = '#p=' + this.state.query.page; // Set up the URL with the necessary variables
      // Do filters
      let filterArr = [];
      for (var prop in this.state.filterOptions) {
        if (prop === 'postType') {
          for (let i=0; i<this.state.filterOptions[prop].length; i++) {
            if (this.state.filterOptions[prop][i].active === true) {
              filterArr.push({
                type: 'postType',
                value: this.state.filterOptions[prop][i].value
              });
            }
          }
        }

        if (prop === 'capability' || prop === 'categories' || prop === 'topics' || prop === 'resourceType') {
          for (let i=0; i<this.state.filterOptions[prop].length; i++) {
            if (this.state.filterOptions[prop][i].active === true) {
              filterArr.push({
                type: prop,
                value: this.state.filterOptions[prop][i].value
              });
            }
          }
        }

        if (prop === 'date') {
          for (let i=0; i<this.state.filterOptions[prop].length; i++) {
            if (this.state.filterOptions[prop][i].active === true && this.state.filterOptions[prop][i].value !== 'all') {
              filterArr.push({
                type: 'date',
                value: this.state.filterOptions[prop][i].value
              });
            }
          }
        }
      }
      for (let i=0; i<filterArr.length; i++) {
        url += '&' + filterArr[i].type + '=' + filterArr[i].value;
      }
      window.history.pushState(historyState, '', url); // Set variables to the window history
    };

  //
  //  FIRST RUN STUFF / TAXONOMY FETCHING
  //
    areTaxonomiesLoaded() { // Checks if every taxonomy fetch has been successfully completed, returns a boolean
      if (this.state.wpData.dataLoaded === false) {
        // Create an array of 0 for each taxonomy we need to load. It'll be changed to 1 if the taxonomy is loaded
        let taxLoaded = (function() {
          let arr = [];
          for (let i=0; i<this.state.wpData.taxonomies.length; i++) {
            arr.push(0)
          }
          return arr;
        }.bind(this))();
        // Check each taxonomy to see if its data has loaded yet. If so, set the item in taxLoaded arrary to 1
        for (let i=0; i<this.state.wpData.taxonomies.length; i++) {
          if (this.state.wpData.taxonomies[i].terms !== null) {
            taxLoaded[i] = 1;
          }
        }

        if(taxLoaded.indexOf(0) === -1) { // If there aren't any zeros, taxLoaded is an hero <3
          let newWpData = this.state.wpData;
          newWpData.dataLoaded = true;
          this.setState({
            wpData: newWpData
          });
          return true;
        } else {
          return false;
        }
      }
    };
    fetchTaxonomies() { // Called by componentDidMount() to fetch taxonomies on load
      for (let i=0; i<this.state.wpData.taxonomies.length; i++) {
        // Format a request the WP API will understand
        let request = this.props.apiURLBase + this.state.wpData.taxonomies[i].name + '?per_page=100'; // without per-page we're limited to 10

        fetch(request, {timeout: 5000}).then(
          response => response.json()
        ).then(
          responseData => { // Success!
            let newWpData = this.state.wpData;
            newWpData.taxonomies[i].terms = responseData;
            this.setState({
              wpData: newWpData,
            });
          }
        ).catch(function(error) { // network request failed / timeout 
          console.log('fetch error: ');
          console.log(error);
        });
      }
    };
    firstRunActiveFilterGeneration() {
      // Loop through all filter options and add them to the orderedActiveFilterList if they're active
      let activeFilterList = [];
      for (const key in this.state.filterOptions) {
        if (key === 'postType') {
          // Make sure we're not on an archive page (we don't want to add the post type to the activeFIlterList on these pages because
          // that gives the user the ability to filter by NO post types if they click the active filter button)
          if (this.state.query.pageType !== 'archive') {
            let postTypes = [];
            for (let i=0; i<this.state.filterOptions[key].length; i++) {
              if (this.state.filterOptions[key][i].active === true) {
                postTypes.push(this.state.filterOptions[key][i])
              }
            }
            // Check if all post types are active, if so don't add all of them to activeFilterList (b/c this is the default state for some pages, eg. search)
            if (postTypes.length < this.state.filterOptions[key].length) {
              for (let i=0; i<postTypes.length; i++) {
                activeFilterList.push(postTypes[i])
              }
            }
          }
        }
        else {
          for (let i=0; i<this.state.filterOptions[key].length; i++) {
            if (this.state.filterOptions[key][i].active === true) {
              activeFilterList.push(this.state.filterOptions[key][i])
            }
          }
        }
      }
      
      this.setState({
        orderedActiveFilterList: activeFilterList
      });
    };
    getWordPressArchiveType() { // Returns a knownType property or false
      // Check the body for known types
      let bodyClasses = document.body.classList;
      // Does this class match one in knownQueryTypes?
      for (let i=0; i<this.props.knownQueryTypes.length; i++) {
        if (bodyClasses.contains(this.props.knownQueryTypes[i].className)) { // We found the right knownTypes!
          let type = this.props.knownQueryTypes[i];
          
          // If we're on a taxonomy archive page, pass back what kind of taxonomy archive page we're on.
          if (bodyClasses.contains('tax-topics') || bodyClasses.contains('tax-capability') || bodyClasses.contains('tax-status')) {
            // Find the taxonomy name for this page based on the body class (eg. tax-topics)
            let tax = null;
            for (let c=0; c<bodyClasses.length; c++) {
              if (bodyClasses[c].match(/(tax-)\D+/g)) { // Found the term! trim off the 'term-'
                tax = bodyClasses[c].substring(4, bodyClasses[c].length);
              }
            }
            // Find the term name for this page based on the body class (eg. term-74)
            let term = null;
            for (let c=0; c<bodyClasses.length; c++) {
              if (bodyClasses[c].match(/(term-)\d+/g)) { // Found the term! trim off the 'term-'
                term = parseInt(bodyClasses[c].substring(5, bodyClasses[c].length));
              }
            }

            // Make sure we found both a taxonomy and a term before we apply a filter to the return.
            let filter = null;
            if (tax !== null && term !== null) {
              filter = {
                type: 'taxonomy',
                name: tax,
                value: term
              }
            }

            if (filter !== null) {
              type.filter = filter;
            }
          }
          return type;
        }
      }
      return false;
    };

  //
  //  FETCH POSTS
  //
    fetchPosts(callback) { // First called by init(), sets up additional query params and calls performPostsFetch() which does the actual fetch.
      let query = null;
      if (this.state.query !== null) { // We were able to find a knownQueryTypes, so use the query in state.
        query = _.cloneDeep(this.state.query);
      }
      if (query === null) { // No query was found, so set the error response state and exit.
        this.setState({
          response: 'error'
        });
        return false;
      } 

      else { // Query exists so do the fetch
        // Is it a search query? If so, get the search value from the DOM
        if (query.pageType === 'search') {
          let searchValue = ''; // If no search query is provided, just return all results by searching for empty string
          let searchForm = document.getElementById('search');
          if (searchForm) {
            searchValue = searchForm.value;
          }
          query.searchValue = searchValue;
        }

        // If query doesn't have pagination attached to it, add it.
        if (typeof query.page === 'undefined'){
          query.page = 1;
        }

        // All done, so set the query and perform the fetch
        this.setState({
          query: query
        }, () => this.performPostsFetch(callback) );
      }
    };
    performPostsFetch(callback) {
      // Set up a request URL the WP API will understand based on app state
      let request = this.props.apiURLBase; // looks like 'http://example.com/wp-json/wp/v2/'
      let filterArr = [];

      // If we're on search, grab the term from the search field
      if (this.state.query.pageType === 'search') {
        filterArr.push({
          type: 'search',
          value: encodeURIComponent(this.state.query.searchValue)
        });
      }

      // Check for active filters and pass them to filterArr
      for (var prop in this.state.filterOptions) {
        if (prop === 'postType') {
          for (let i=0; i<this.state.filterOptions[prop].length; i++) {
            if (this.state.filterOptions[prop][i].active === true) {
              filterArr.push({
                type: 'type[]',
                value: this.state.filterOptions[prop][i].value
              });
            }
          }
        }

        if (prop === 'capability' || prop === 'categories' || prop === 'topics' || prop === 'resourceType' || prop === 'status') {
          for (let i=0; i<this.state.filterOptions[prop].length; i++) {
            if (this.state.filterOptions[prop][i].active === true) {
              filterArr.push({
                type: prop + '[]',
                value: this.state.filterOptions[prop][i].value
              });
            }
          }
        }

        if (prop === 'date') {
          for (let i=0; i<this.state.filterOptions[prop].length; i++) {
            if (this.state.filterOptions[prop][i].active === true && this.state.filterOptions[prop][i].value !== 'all') {
              let d = new Date();
              d.setDate(d.getDate()); // Set d to today
              let monthsToSubtract = this.state.filterOptions[prop][i].value * -1;
              d = addMonthsToDate(d, monthsToSubtract); // Subtract the number of months based on the number of months provided from DateFilter
              filterArr.push({
                type: 'after',
                value: d.toISOString()
              });
            }
          }
        }
      }

      // Apply the active filters we found to the request
      request += 'multiple-post-type';
      for (let i=0; i<filterArr.length; i++) {
        if (i === 0) {
          request += '?' + filterArr[i].type + '=' + filterArr[i].value;
        }
        else {
          request += '&' + filterArr[i].type + '=' + filterArr[i].value;
        }
      }

      // Get the right page!
      request += '&page=' + this.state.query.page;
      
      console.log(request);
      let totalPosts = null; // We'll get this number from the response headers
      return fetch(request, {timeout: 5000}).then(
        function(response) {
          totalPosts = parseInt(response.headers.get("X-WP-Total"),10);
          return response.json();
        }
      ).then(
        responseData => { // Success!
          let responseObj = {
            posts: responseData,
            totalPosts: totalPosts
          }
          this.setState({
            response: responseObj,
          }, () => this.executeCallback(callback));
          this.addHistoryState();
        }
      ).catch(function(error) { // network request failed / timeout 
        console.log('fetch error: ');
        console.log(error);
      });
    };
    executeCallback(callback) {
      if (typeof callback === "function") {
        callback();
      }
    };
  

  //
  //  INTERACTIVITY
  //
    handleFilterChange(changeRequest) { // Called when a user changes a filter on the page, bubbled from TaxonomyFilter.js
      let filterOptions = _.cloneDeep(this.state.filterOptions);
      let orderedActiveFilterList = _.cloneDeep(this.state.orderedActiveFilterList);

      let orderedActiveFilterListChange = function(obj, action) {
        if (action === 'add') { // Add item to the list of active filters.
          // Check if post type or date already exists, if so remove it before adding it again.
          if (obj.type === 'postType' || obj.type === 'date') {
            for (let i=0; i<orderedActiveFilterList.length; i++) {
              if (obj.value === orderedActiveFilterList[i].value && obj.type === orderedActiveFilterList[i].type) { // Objects match, so we found the item to remove!
                orderedActiveFilterList.splice(i, 1);
              }
            }
          }
          else if (obj.type === 'taxonomy') {
            // Only add it to the list if it doesn't already exist
            for (let i=0; i<orderedActiveFilterList.length; i++) {
              if (obj.value === orderedActiveFilterList[i].value && obj.type === orderedActiveFilterList[i].type) { // Objects match, so we found the item to remove!
                orderedActiveFilterList.splice(i, 1);
              }
            } 
          }
          orderedActiveFilterList.push(obj);
        }

        else {
          if (obj.type === 'taxonomy') {
            for (let i=0; i<orderedActiveFilterList.length; i++) { 
              if (_.isEqual(obj.obj, orderedActiveFilterList[i].obj)) { // Objects match, so we found the item to remove!
                orderedActiveFilterList.splice(i, 1);
              }
            }
          }

          if (obj.type === 'postType' || obj.type === 'date') {
            for (let i=0; i<orderedActiveFilterList.length; i++) { 
              if (obj.value === orderedActiveFilterList[i].value && obj.type === orderedActiveFilterList[i].type) { // Objects match, so we found the item to remove!
                orderedActiveFilterList.splice(i, 1);
              }
            } 
          }
        }
      };

      // Multi-select
      if (typeof changeRequest.obj.length !== 'undefined') { // Multi-selects return an array instead of an object, so they have a length. This is how we identify them.
        if (changeRequest.action === 'activate') {
          
          if (changeRequest.obj[0].type === 'taxonomy') { // Check the first array item's type to see what kind of filter we're changing
            let taxonomyName = changeRequest.obj[0].taxonomy.name;
            for (let i=0; i<filterOptions[taxonomyName].length; i++) { // Loop through the taxonomny terms and deactivate/activate the appropriate items based on what the multi-select returned
              // Check if this taxonomy item matches one returned by the multi-select, and if so do stuff with it.
              let active = false;
              let orderedActiveFilterListAction = 'remove';
              for (let j=0; j<changeRequest.obj.length; j++) {
                if (_.isEqual(filterOptions[taxonomyName][i].obj, changeRequest.obj[j].obj)) { // Match! This item should be activated
                  active = true;
                  orderedActiveFilterListAction = 'add';
                }
              }
              filterOptions[taxonomyName][i].active = active;
              orderedActiveFilterListChange(filterOptions[taxonomyName][i], orderedActiveFilterListAction);
            }
          }
        }
      }

      // Single select
      else { // changeReqeust.obj has no length, so it's a plain object. Do the default single select logic.
        if (changeRequest.action === 'activate') {
          // What type are we trying to activate?
          if (changeRequest.obj.type === 'taxonomy') {
            let taxonomyName = changeRequest.obj.taxonomy.name;
            // Are we activating a child or a parent?
            if(changeRequest.obj.relation === 'parent') {
              for (let i=0; i<filterOptions[taxonomyName].length; i++) { // Deactivate any active taxonomy terms and activate the new one
                if (filterOptions[taxonomyName][i].active === true) { // Deactivate old
                  filterOptions[taxonomyName][i].active = false;
                  orderedActiveFilterListChange(filterOptions[taxonomyName][i], 'remove');
                }
                if (filterOptions[taxonomyName][i].value === changeRequest.obj.value) { // Activate new
                  filterOptions[taxonomyName][i].active = true;
                  orderedActiveFilterListChange(filterOptions[taxonomyName][i], 'add');
                }
              }
            }
            if(changeRequest.obj.relation === 'child') { // For children we just activate the new one deactivate others with the same parent
              let parent = changeRequest.obj.parent;
              for (let i=0; i<filterOptions[taxonomyName].length; i++) { // Deactivate any active taxonomy terms and activate the new one
                if (filterOptions[taxonomyName][i].value === changeRequest.obj.value) { // Activate new
                  filterOptions[taxonomyName][i].active = true;
                  orderedActiveFilterListChange(filterOptions[taxonomyName][i], 'add');
                }
                else {
                  if (filterOptions[taxonomyName][i].parent === parent) {
                    filterOptions[taxonomyName][i].active = false;
                    orderedActiveFilterListChange(filterOptions[taxonomyName][i], 'remove');
                  }
                }
              }
            }
          }

          if (changeRequest.obj.type === 'postType') {
            for (let i=0; i<filterOptions.postType.length; i++) {
              if (filterOptions.postType[i].value !== changeRequest.obj.value) { // Only one post type can be active at a time, so deactivate all others
                filterOptions.postType[i].active = false; 
                orderedActiveFilterListChange(filterOptions.postType[i], 'remove');

                // Check if this is a parent, and if so deactivate its children
                if (filterOptions.postType[i].relation === 'parent') {
                  for (let c=0; c<filterOptions.postType[i].children.length; c++) {
                    if (filterOptions.postType[i].children[c].active === true) {
                      filterOptions.postType[i].children[c].active = false;
                      orderedActiveFilterListChange(filterOptions.postType[i].children[c], 'remove');
                    }  
                  }                
                }
              }
              else { 
                filterOptions.postType[i].active = true;
                orderedActiveFilterListChange(filterOptions.postType[i], 'add');
              }
            }
          }

          if (changeRequest.obj.type === 'date') {
            for (let i=0; i<filterOptions.date.length; i++) {
              if (filterOptions.date[i].value === changeRequest.obj.value) { // Activate new
                filterOptions.date[i].active = true;
                orderedActiveFilterListChange(filterOptions.date[i], 'add');
              }
              else { // Deactivate old
                filterOptions.date[i].active = false;
                orderedActiveFilterListChange(filterOptions.date[i], 'remove');
              }
            }
          }
        }

        if (changeRequest.action === 'deactivate') {
          // What type are we trying to deactivate?
          if (changeRequest.obj.type === 'taxonomy') {
            let taxonomyName = changeRequest.obj.taxonomy.name;
            for (let i=0; i<filterOptions[taxonomyName].length; i++) {
              if (filterOptions[taxonomyName][i].value === changeRequest.obj.value) { // Deactivate this fellow
                filterOptions[taxonomyName][i].active = false;
                orderedActiveFilterListChange(filterOptions[taxonomyName][i], 'remove');
              }
              // Check if this is a parent, and if so deactivate its children
              if (changeRequest.obj.relation === 'parent') {
                if (filterOptions[taxonomyName][i].obj.parent === changeRequest.obj.value) {
                  filterOptions[taxonomyName][i].active = false;
                  orderedActiveFilterListChange(filterOptions[taxonomyName][i], 'remove');
                }
              }
            }
          }

          if (changeRequest.obj.type === 'postType') {
            for (let i=0; i<filterOptions.postType.length; i++) {
              if (filterOptions.postType[i].value === changeRequest.obj.value) { // Deactivate this fellow
                filterOptions.postType[i].active = false;
                orderedActiveFilterListChange(filterOptions.postType[i], 'remove');

                // Check if this is a parent, and if so deactivate its children
                if (changeRequest.obj.relation === 'parent') {
                  console.log('is parent!');
                  for (let c=0; c<filterOptions.postType[i].children.length; c++) {
                    filterOptions.postType[i].children[c].active = false;
                    orderedActiveFilterListChange(filterOptions.postType[i].children[c], 'remove');
                  } 
                }
              }
            }
            // If we're on a page that should show all posts by default, reset all to active.
            if (this.state.query.postType === 'all') {
              for (let i=0; i<filterOptions.postType.length; i++) {
                filterOptions.postType[i].active = true;
                // orderedActiveFilterListChange(filterOptions.postType[i], 'add');
              }
            }
          }

          if (changeRequest.obj.type === 'date') {
            for (let i=0; i<filterOptions.date.length; i++) {
              if (filterOptions.date[i].value === changeRequest.obj.value) { // Deactivate this fellow
                filterOptions.date[i].active = false;
                orderedActiveFilterListChange(filterOptions.date[i], 'false');
              }
            }
          }
        }
      }

      // Regardlesss of the filter action, we need to reset pagination every time a filter is performed.
      let query = _.cloneDeep(this.state.query);
      query.page = 1;

      // Set the state and perform a new fetch with the new filters we've set
      this.setState({
        filterOptions: filterOptions,
        orderedActiveFilterList: orderedActiveFilterList,
        query: query,
      }, () => this.fetchPosts() );
    };

    handlePaginationClick(data) { // Called when a user clicks a pagination button
      let newQuery = this.state.query;
      newQuery.page = data;
      this.setState({
        query: newQuery,
      }, () => this.fetchPosts(this.scrollToTop()) );
    };

    scrollToTop() {
      var scroll = Scroll.animateScroll;
      scroll.scrollToTop({'duration': 20});
    };

  render() {
    return (
      <div className="Archive">
        <div className="Archive__container">
          <Sidebar applyFilter={ function(data) { this.handleFilterChange(data) }.bind(this) } filterOptions={this.state.filterOptions} query={this.state.query} wpData={this.state.wpData} />
          <PostList applyFilter={ function(data) { this.handleFilterChange(data) }.bind(this) } orderedActiveFilterList={this.state.orderedActiveFilterList} pagination={this.state.pagination} paginationClick={function(data) { this.handlePaginationClick(data); }.bind(this)} query={this.state.query} response={this.state.response} wpData={this.state.wpData} />
        </div>
      </div>
    );
  };
};

Archive.propTypes = {
  apiURLBase: PropTypes.string,
  knownQueryTypes: PropTypes.array,
  pagination: PropTypes.object,
};

Archive.defaultProps = {
  apiURLBase: (function() {
    if (window.location.hostname === 'localhost') {
      return '//dev-jba.colorcrate.com/wp-json/wp/v2/' // Digital Ocean dev api base
    }
    else {
      return window.location.origin + '/wp-json/wp/v2/';
    }
  })(),
  filterOptions: {
    capability: [], // Fetched from WordPress
    categories: [], // Fetched from WordPress
    date: [
      {
        active: false,
        label: 'All time',
        type: 'date',
        value: 1200, // Big number of months to serve as "all time" value
      },
      {
        active: false,
        label: 'Past 3 years',
        type: 'date',
        value: 36,
      },
      {
        active: false,
        label: 'Past year',
        type: 'date',
        value: 12,
      },
    ],
    status: [], // Fetched from WordPress
    topics: [], // Fetched from WordPress
    postType: [
      {
        active: false,
        children: [],
        label: 'News & Events',
        type: 'postType',
        value: 'news-events',
      },
      {
        active: false,
        children: [],
        label: 'Projects',
        type: 'postType',
        value: 'project',
      },
      {
        active: false,
        children: [],
        label: 'Resources',
        type: 'postType',
        value: 'resource',
      },
    ],
    resourceType: [], // Fetched from WordPress
  },
  knownQueryTypes: [
    {
      'className': 'post-type-archive-resource',
      'postType': 'resource',
      'pageType': 'archive',
    },
    {
      'className': 'post-type-archive-project',
      'postType': 'project',
      'pageType': 'archive',
    },
    {
      'className': 'post-type-archive-news-events',
      'postType': 'news-events',
      'pageType': 'archive',
    },
    {
      'className': 'search',
      'postType': 'all',
      'pageType': 'search',
    },
    {
      'className': 'archive',
      'postType': 'all',
      'pageType': 'archive',
    },
  ],
  pagination: {
    perPage: 10,
    currentPage: 1
  }
};



export default Archive;


// WEBPACK FOOTER //
// ./src/Archive.js